import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { LOCALES, type Locale } from '@/i18n';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';

const COOKIE_KEY = 'vyxora_lang';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 ano

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`;
}

function detectBrowserLocale(): Locale | null {
  if (typeof navigator === 'undefined') return null;
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith('en')) return 'en-US';
  if (lang.startsWith('es')) return 'es';
  if (lang.startsWith('pt')) return 'pt-BR';
  return null;
}

/**
 * Sugere trocar o idioma quando o navegador do usuário indica preferência
 * diferente do pt-BR (padrão) e ele ainda não escolheu.
 */
export function LanguageSuggestionBanner() {
  const { t } = useTranslation('common');
  const { currentLocale, switchLocale } = useLocalizedNavigate();
  const [suggestion, setSuggestion] = useState<Exclude<Locale, 'pt-BR'> | null>(null);

  useEffect(() => {
    // Só sugere se: (1) não há cookie ainda (2) idioma corrente é pt-BR
    // (3) navegador prefere en ou es
    if (readCookie(COOKIE_KEY)) return;
    if (currentLocale !== 'pt-BR') return;
    const browser = detectBrowserLocale();
    if (browser && browser !== 'pt-BR' && LOCALES.includes(browser)) {
      setSuggestion(browser as Exclude<Locale, 'pt-BR'>);
    }
  }, [currentLocale]);

  if (!suggestion) return null;

  const message = t(`languageBanner.${suggestion}.message`);
  const accept = t(`languageBanner.${suggestion}.accept`);
  const dismiss = t(`languageBanner.${suggestion}.dismiss`);

  return (
    <div className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-2xl rounded-2xl border border-vyxora-cyan-400/30 bg-vyxora-panel/95 p-4 shadow-glow-strong backdrop-blur-lg md:inset-x-auto md:right-8">
      <div className="flex items-start gap-4">
        <span className="mt-1 text-2xl">{suggestion === 'en-US' ? '🇺🇸' : '🇪🇸'}</span>
        <div className="flex-1">
          <p className="text-sm text-vyxora-text-light">{message}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => {
                writeCookie(COOKIE_KEY, suggestion);
                switchLocale(suggestion);
                setSuggestion(null);
              }}
              className="rounded-md bg-vyxora-cyan-400 px-4 py-2 text-xs font-semibold text-vyxora-abyss hover:brightness-110"
            >
              {accept}
            </button>
            <button
              onClick={() => {
                writeCookie(COOKIE_KEY, 'pt-BR');
                setSuggestion(null);
              }}
              className="rounded-md border border-white/10 px-4 py-2 text-xs font-semibold text-vyxora-text-light/70 hover:border-white/30 hover:text-vyxora-text-light"
            >
              {dismiss}
            </button>
          </div>
        </div>
        <button
          onClick={() => {
            writeCookie(COOKIE_KEY, 'pt-BR');
            setSuggestion(null);
          }}
          aria-label="Fechar"
          className="rounded p-1 text-vyxora-text-light/50 hover:bg-white/5 hover:text-vyxora-text-light"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
